import React, {useCallback} from 'react';

import {
  ProgressDeepResearchMessage,
  ProgressMessage,
  ProgressData,
} from 'llm/deep-research/messages/DeepResearchProgress';
import {ReportDeepResearchMessage, ReportMessage} from 'llm/deep-research/messages/DeepResearchReport';
import {UserDeepResearchMessage, UserMessage} from 'llm/deep-research/messages/DeepResearchUserMessage';
import {DeepResearchMessageType} from 'llm/deep-research/types';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

export type DeepResearchMessage =
  | UserDeepResearchMessage
  | ReportDeepResearchMessage
  | ProgressDeepResearchMessage;

export interface DeepResearchMessageComponentProps {
  message: DeepResearchMessage;
  progressData: ProgressData | null;
  openActivityModal: (progressData: ProgressData, selectedToolId?: string) => void;
}

export function DeepResearchMessageComponent({
  message,
  progressData,
  openActivityModal,
}: DeepResearchMessageComponentProps): JSX.Element | null {
  const handleCitationClick = useCallback(
    (toolId: string) => {
      if (!progressData) {
        logging.error('[llm] Citation clicked but no progress data available', {
          extra: {toolId},
        });
        return;
      }

      openActivityModal(progressData, toolId);
    },
    [progressData, openActivityModal]
  );

  switch (message.type) {
    case DeepResearchMessageType.USER_MESSAGE:
      return <UserMessage message={message} />;
    case DeepResearchMessageType.PROGRESS: {
      if (!progressData) {
        logging.error('[llm] Progress message missing required progress data', {
          extra: {
            messageType: message.type,
            status: message.status,
          },
        });
        return <></>;
      }

      return (
        <ProgressMessage
          message={message}
          progressData={progressData}
          openActivityModal={openActivityModal}
        />
      );
    }
    case DeepResearchMessageType.SYSTEM_REPORT: {
      return <ReportMessage message={message} onCitationClick={handleCitationClick} />;
    }
    default:
      assertNever(message);
      throw new Error(`Unexpected message type`);
  }
}
